import React, {useEffect, useMemo, useState} from 'react'
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from 'chart.js'
import {Chart} from 'react-chartjs-2'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../components/Toast/Toast'
import {getProductionAnalytics, operacije as getOperacije} from '../../api/plaviNalozi'
import {PlaviNalogOperacija, PlaviNalogProductionAnalytics} from '../../models/PlaviNalog'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

type Props = {
    orderId: number
}

const formatNumber = (value?: number) =>
    Number(value ?? 0).toLocaleString('bs-BA', {maximumFractionDigits: 2})

const formatPercent = (value?: number) => `${Number(value ?? 0).toFixed(2)}%`

const toNumber = (value?: number | string) => {
    if (value === undefined || value === null || value === '') {
        return undefined
    }

    const numberValue = Number(value)

    return Number.isFinite(numberValue) ? numberValue : undefined
}

const getBrojOperacija = (value?: number | string) => {
    const brojOperacija = toNumber(value)

    return brojOperacija && brojOperacija > 0 ? brojOperacija : 1
}

const getOperationId = (operation: PlaviNalogOperacija) =>
    toNumber(operation.idOperacije) ?? toNumber(operation.operacija?.id) ?? toNumber(operation.id)

const getBrojOperacijaZaOperaciju = (operation: PlaviNalogOperacija) =>
    getBrojOperacija(operation.operacija?.brojOperacija ?? operation.brojOperacija)

const getCompletedGoodsQuantity = (
    rows: PlaviNalogProductionAnalytics[],
    requiredOperations: PlaviNalogOperacija[]
) => {
    const operationTotals = new Map<number, {quantity: number; brojOperacija: number}>()

    requiredOperations.forEach((operation) => {
        const operationId = getOperationId(operation)

        if (operationId === undefined) {
            return
        }

        operationTotals.set(operationId, {
            quantity: 0,
            brojOperacija: getBrojOperacijaZaOperaciju(operation),
        })
    })

    rows.forEach((row) => {
        row.operacije?.forEach((operation) => {
            const previous = operationTotals.get(operation.idOperacije)
            const quantity = Number(operation.komada ?? 0)
            const brojOperacija = getBrojOperacija(operation.brojOperacija)

            operationTotals.set(operation.idOperacije, {
                quantity: (previous?.quantity ?? 0) + quantity,
                brojOperacija: previous?.brojOperacija ?? brojOperacija,
            })
        })
    })

    if (operationTotals.size === 0) {
        return rows.reduce((sum, row) => sum + Number(row.ukupnoKomada ?? 0), 0)
    }

    return Math.min(
        ...Array.from(operationTotals.values()).map((operation) => operation.quantity / operation.brojOperacija)
    )
}

const formatMinutesAsTime = (minutes: number) => {
    const totalSeconds = Math.round(minutes * 60)
    const hours = Math.floor(totalSeconds / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const efficiencyClass = (value: number) => {
    if (value < 80) {
        return 'danger'
    }
    if (value < 95) {
        return 'warning'
    }
    if (value <= 105) {
        return 'success'
    }
    return 'primary'
}

const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
    },
    scales: {
        x: {
            beginAtZero: true,
        },
    },
}

const buildBarData = (label: string, color: string, rows: PlaviNalogProductionAnalytics[], value: (row: PlaviNalogProductionAnalytics) => number) => ({
    labels: rows.map((row) => row.ime),
    datasets: [
        {
            label,
            data: rows.map(value),
            backgroundColor: color,
            borderRadius: 4,
        },
    ],
})

const ProductionAnalyticsModal: React.FC<Props> = ({orderId}) => {
    const [data, setData] = useState<PlaviNalogProductionAnalytics[]>([])
    const [operations, setOperations] = useState<PlaviNalogOperacija[]>([])
    const [loading, setLoading] = useState(false)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        if (loaded) {
            return
        }

        setLoading(true)
        Promise.all([getProductionAnalytics(orderId), getOperacije(orderId)])
            .then(([analyticsResponse, operationsResponse]) => {
                setData(Array.isArray(analyticsResponse) ? analyticsResponse : [])
                setOperations(Array.isArray(operationsResponse) ? operationsResponse : [])
                setLoaded(true)
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja analitike.')
            )
            .finally(() => setLoading(false))
    }, [loaded, orderId])

    const totals = useMemo(() => {
        const totalEmployees = data.length
        const totalQuantity = getCompletedGoodsQuantity(data, operations)
        const totalMinutes = data.reduce((sum, row) => sum + Number(row.ukupnoVrijemeMinuta ?? 0), 0)
        const weightedEfficiency =
            totalMinutes > 0
                ? data.reduce((sum, row) => sum + Number(row.prosjecnaUcinkovitost ?? 0) * Number(row.ukupnoVrijemeMinuta ?? 0), 0) / totalMinutes
                : 0

        return {
            totalEmployees,
            totalQuantity,
            totalMinutes,
            weightedEfficiency,
        }
    }, [data, operations])

    const quantityRows = useMemo(
        () => [...data].sort((a, b) => Number(b.ukupnoKomada ?? 0) - Number(a.ukupnoKomada ?? 0)),
        [data]
    )
    const efficiencyRows = useMemo(
        () => [...data].sort((a, b) => Number(b.prosjecnaUcinkovitost ?? 0) - Number(a.prosjecnaUcinkovitost ?? 0)),
        [data]
    )
    const timeRows = useMemo(
        () => [...data].sort((a, b) => Number(b.ukupnoVrijemeMinuta ?? 0) - Number(a.ukupnoVrijemeMinuta ?? 0)),
        [data]
    )

    const bestEfficiencyId = efficiencyRows[0]?.idUposlenik
    const mostProducedId = quantityRows[0]?.idUposlenik
    const mostTimeId = timeRows[0]?.idUposlenik
    const leaderboard = efficiencyRows.slice(0, 3)
    const medalLabels = ['1.', '2.', '3.']

    return (
        <div>
            <div className='mb-8'>
                <h2 className='fw-bold mb-1'>Analitika proizvodnje</h2>
                <div className='text-muted'>Pregled učinka radnika za plavi nalog.</div>
            </div>
                {loading ? (
                    <LoadingComponent />
                ) : data.length === 0 ? (
                    <div className='text-center text-muted py-10'>Nema podataka za analitiku.</div>
                ) : (
                    <>
                        <div className='row g-5 mb-8'>
                            <div className='col-md-3'>
                                <div className='border rounded p-5 h-100'>
                                    <div className='text-muted fw-semibold'>Ukupno radnika</div>
                                    <div className='fs-2 fw-bold'>{totals.totalEmployees}</div>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className='border rounded p-5 h-100'>
                                    <div className='text-muted fw-semibold'>Završeno komada</div>
                                    <div className='fs-2 fw-bold'>{formatNumber(totals.totalQuantity)}</div>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className='border rounded p-5 h-100'>
                                    <div className='text-muted fw-semibold'>Ukupno vrijeme</div>
                                    <div className='fs-2 fw-bold'>{formatMinutesAsTime(totals.totalMinutes)}</div>
                                </div>
                            </div>
                            <div className='col-md-3'>
                                <div className='border rounded p-5 h-100'>
                                    <div className='text-muted fw-semibold'>Prosječna učinkovitost</div>
                                    <div className='fs-2 fw-bold'>{formatPercent(totals.weightedEfficiency)}</div>
                                </div>
                            </div>
                        </div>

                        <div className='card border mb-8'>
                            <div className='card-header'>
                                <div className='card-title fw-bold'>Rang lista</div>
                            </div>
                            <div className='card-body py-4'>
                                <div className='row g-4'>
                                    {leaderboard.map((row, index) => (
                                        <div className='col-md-4' key={row.idUposlenik}>
                                            <div className='border rounded p-4 h-100'>
                                                <div className='fs-4 fw-bold'>
                                                    {medalLabels[index]} {row.ime}
                                                </div>
                                                <div className='text-muted'>{formatPercent(row.prosjecnaUcinkovitost)}</div>
                                                <div className='fw-semibold'>{formatNumber(row.ukupnoKomada)} kom</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className='row g-5 mb-8'>
                            <div className='col-12'>
                                <div className='card border'>
                                    <div className='card-header'><div className='card-title fw-bold'>Ukupna količina po radniku</div></div>
                                    <div className='card-body' style={{height: Math.max(260, quantityRows.length * 42)}}>
                                        <Chart type='bar' data={buildBarData('Ukupna količina', '#3E97FF', quantityRows, (row) => Number(row.ukupnoKomada ?? 0))} options={chartOptions} />
                                    </div>
                                </div>
                            </div>
                            <div className='col-12'>
                                <div className='card border'>
                                    <div className='card-header'><div className='card-title fw-bold'>Prosječna učinkovitost po radniku</div></div>
                                    <div className='card-body' style={{height: Math.max(260, efficiencyRows.length * 42)}}>
                                        <Chart type='bar' data={buildBarData('Učinkovitost %', '#50CD89', efficiencyRows, (row) => Number(row.prosjecnaUcinkovitost ?? 0))} options={chartOptions} />
                                    </div>
                                </div>
                            </div>
                            <div className='col-12'>
                                <div className='card border'>
                                    <div className='card-header'><div className='card-title fw-bold'>Ukupno vrijeme proizvodnje po radniku</div></div>
                                    <div className='card-body' style={{height: Math.max(260, timeRows.length * 42)}}>
                                        <Chart type='bar' data={buildBarData('Minute', '#F1416C', timeRows, (row) => Number(row.ukupnoVrijemeMinuta ?? 0))} options={chartOptions} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='accordion' id='plavi-nalog-analytics-accordion'>
                            {data.map((row) => (
                                <div className='accordion-item' key={row.idUposlenik}>
                                    <h2 className='accordion-header' id={`analytics-heading-${row.idUposlenik}`}>
                                        <button
                                            className='accordion-button collapsed'
                                            type='button'
                                            data-bs-toggle='collapse'
                                            data-bs-target={`#analytics-collapse-${row.idUposlenik}`}
                                        >
                                            <span className='fw-bold me-4'>{row.ime}</span>
                                            <span className='badge badge-light-primary me-2'>{formatNumber(row.ukupnoKomada)} kom</span>
                                            <span className='badge badge-light-success me-2'>{formatPercent(row.prosjecnaUcinkovitost)}</span>
                                            <span className='badge badge-light-info me-2'>{row.ukupnoVrijeme}</span>
                                            {row.idUposlenik === bestEfficiencyId && <span className='badge badge-light-warning me-2'>Najbolji učinak</span>}
                                            {row.idUposlenik === mostProducedId && <span className='badge badge-light-primary me-2'>Najviše proizvedeno</span>}
                                            {row.idUposlenik === mostTimeId && <span className='badge badge-light-danger me-2'>Najduže radio</span>}
                                        </button>
                                    </h2>
                                    <div
                                        id={`analytics-collapse-${row.idUposlenik}`}
                                        className='accordion-collapse collapse'
                                        data-bs-parent='#plavi-nalog-analytics-accordion'
                                    >
                                        <div className='accordion-body'>
                                            <div className='table-responsive'>
                                                <table className='table align-middle table-bordered gy-5 no-footer'>
                                                    <thead>
                                                        <tr className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                                                            <th className='text-white'>Operacija</th>
                                                            <th className='text-white'>Količina</th>
                                                            <th className='text-white'>Vrijeme</th>
                                                            <th className='text-white'>Učinkovitost</th>
                                                            <th className='text-white'>Prijave</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.operacije?.length > 0 ? (
                                                            row.operacije.map((operation) => {
                                                                const progressClass = efficiencyClass(Number(operation.prosjecnaUcinkovitost ?? 0))

                                                                return (
                                                                    <tr key={operation.idOperacije}>
                                                                        <td>{operation.operacija}</td>
                                                                        <td>{formatNumber(operation.komada)}</td>
                                                                        <td>{operation.vrijeme}</td>
                                                                        <td style={{minWidth: 220}}>
                                                                            <div className='d-flex align-items-center gap-3'>
                                                                                <div className='progress flex-grow-1 h-8px'>
                                                                                    <div
                                                                                        className={`progress-bar bg-${progressClass}`}
                                                                                        role='progressbar'
                                                                                        style={{width: `${Math.min(Number(operation.prosjecnaUcinkovitost ?? 0), 120)}%`}}
                                                                                    />
                                                                                </div>
                                                                                <span className='fw-bold'>{formatPercent(operation.prosjecnaUcinkovitost)}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td>{operation.brojPrijava ?? '-'}</td>
                                                                    </tr>
                                                                )
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={5} className='text-center text-muted py-6'>
                                                                    Nema operacija.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
        </div>
    )
}

export default ProductionAnalyticsModal
